import { IIntakeIdWithDates, FilterKey } from 'api-paths-cas';
import { all, call, cancelled, fork, put, select, takeLatest, getContext } from 'redux-saga/effects';
import { MATCH_FILTER_NAMES } from 'shared-components';
import * as api from '../../../api/api';
import { IConfigState } from '../../../types';
import { QueryKeys } from '@/types';
import { INTAKE_ALL_END_DATE, INTAKE_ALL_START_DATE } from '../../../utilities/constants';
import { getActiveStageTab, getDaysAgoInEpochMilliseconds } from '../../../utilities/helpers';
import isTesting from '../../../utilities/isTesting';
import { hasPerm, P } from '../../../utilities/roles/permissions';
import { logout } from '../../auth/actions';
import { AuthState, UserRole } from '../../auth/types';
import { getAuth, getConfig, getIntakeId, getIntakeRange, getUI } from '../../selectors';
import { setSelectedRows, getStudentsFailure, getStudentsSuccess, getAllStudentsComplete } from '../actions';
import { DashboardActionTypes } from '../types';
import { shouldSetUniFromState } from './helpers';
import { InstitutionEnum } from '@/components/molecules/ActionsToolbar/types';

export let getAllApplicationsAbortController: AbortController;

export function* getApplicationsSearchParams() {
  const auth: AuthState = yield select(getAuth);
  const config: IConfigState = yield select(getConfig);
  const intakeId: string = yield select(getIntakeId);
  if (!intakeId && !isTesting) {
    return;
  }
  const { headerTab } = yield select(getUI);
  const intakeRange: IIntakeIdWithDates | undefined = yield select(getIntakeRange, intakeId!);
  const context = yield getContext('sagaContext');
  const idToken: any = yield call(context.getIdToken);

  return createGetAllApplicationsParams({
    auth,
    config,
    intakeId,
    headerTab,
    intakeRange,
    idToken,
  }) as api.IGetAllParams;
}

export const createGetAllApplicationsParams = ({
  auth,
  config,
  intakeId,
  headerTab,
  intakeRange,
  idToken,
}: {
  auth: AuthState;
  config: IConfigState;
  intakeId: string;
  headerTab: any;
  intakeRange: IIntakeIdWithDates | undefined;
  idToken: string;
}) => {
  const params = new URLSearchParams(window.location.search);

  if (!intakeId && !isTesting) {
    return;
  }

  // Check if uni should be set for this user role
  const shouldSetUni = shouldSetUniFromState(auth.role);
  // const isAgent = auth.role.includes(UserRole.Agent) || auth.role.includes(UserRole.AgencyAdmin);

  const uniParam = params.get(QueryKeys.UNI);

  let uni: string | undefined;
  if (shouldSetUni && uniParam) {
    uni = uniParam as string;
  } else if (!shouldSetUni) {
    uni = auth.uni;
  }

  const isPathway = config.isPathway ?? false;

  const startDate = intakeRange?.startDate ?? INTAKE_ALL_START_DATE;
  const endDate = intakeRange?.endDate ?? INTAKE_ALL_END_DATE;

  const canViewIncludeStudentDropdown = !!(
    config.relatedPathways?.length &&
    hasPerm(auth.role, P.VIEW_INSTITUTION_DROPDOWN) &&
    !(
      [UserRole.UniAdmin, UserRole.Uni, UserRole.UniSecure, UserRole.UniLite].includes(auth.role) &&
      auth.pathwayAccess === 'None'
    )
  );

  // Determine if user is an agent
  const isAgent = auth.role === UserRole.Agent || auth.role === UserRole.AgencyAdmin;

  if (isAgent) {
    console.log('Agent user detected:', { role: auth.role, agencyId: auth.agencyId });
  }

  getAllApplicationsAbortController = new AbortController();
  return {
    abortController: getAllApplicationsAbortController,
    idToken,
    stage: decodeURI(getActiveStageTab(params, headerTab)),
    ...(isPathway ? { pathway: uni } : { uni }),
    intakeId,
    pathwayVisibility: canViewIncludeStudentDropdown
      ? (params.get('institution') ??
        localStorage.getItem('dashboard-uni-institution-value') ??
        InstitutionEnum.INSTITUTION_UNI)
      : '',
    startDate,
    endDate,
    page: Number.parseInt(params.get('page') || '1', 10),
    sortCol: params.get('sort-col'),
    sortDir: params.get('sort-dir'),
    search: params.get('search'),
    unreadMessages: params.get('unread_messages'),
    unis: params.getAll(FilterKey.UNIVERSITY),
    studentNo: params.getAll('filter_student_number'),
    surname: params.getAll('filter_surname'),
    givenName: params.getAll('filter_given_name'),
    course: params.getAll(FilterKey.COURSE),
    faculty: params.getAll(FilterKey.FACULTY),
    nationality: params.getAll(FilterKey.NATIONALITY),
    country: params.getAll(FilterKey.COUNTRY_OF_RESIDENCE),
    applicationCountry: params.getAll(FilterKey.APPLICATION_COUNTRY),
    activityStatus: params.getAll(FilterKey.ACTIVITY_STATUS),
    idle: params.getAll('filter_IDLE'),
    complete: params.getAll(FilterKey.COMPLETE),
    incomplete: params.getAll(FilterKey.INCOMPLETE),
    fundsMaturity: params.getAll(FilterKey.FUNDS_MATURITY),
    //region: params.getAll('filter_REGION'),
    market: params.getAll(FilterKey.MARKET),
    visaStatus: params.getAll(FilterKey.VISA_STATUS),
    confidence: params.getAll(FilterKey.CONFIDENCE).filter(item => item !== 'any'),
    agent: isAgent && auth.agencyId ? [auth.agencyId] : params.getAll(FilterKey.AGENT_NAME),
    conditionalStatus: params.getAll(FilterKey.CONDITIONAL_STATUS),
    courseConditionalStatus: params.getAll(FilterKey.COURSE_CONDITIONAL_STATUS),
    evidenceRequirements: params.getAll(FilterKey.EVIDENCE_REQUIREMENTS),
    customRegion: params.getAll(FilterKey.RISK_REGION),
    tag: params.getAll(FilterKey.TAGS),
    programmableTag: params.getAll(FilterKey.PROGRAMMABLE_TAGS),
    services: params.getAll(FilterKey.SERVICES),
    immigrationProviders: params.getAll(FilterKey.IMMIGRATION_PROVIDERS),
    interview: params.getAll(FilterKey.INTERVIEW),
    recordedInterview: params.getAll(FilterKey.RECORDED_INTERVIEW),
    idleFrom: params.has(FilterKey.MAXIMUM_DAYS_IDLE) && [
      getDaysAgoInEpochMilliseconds(params.getAll(FilterKey.MAXIMUM_DAYS_IDLE).map(Number)[0]),
    ],
    idleTo: params.has(FilterKey.MINIMUM_DAYS_IDLE) && [
      getDaysAgoInEpochMilliseconds(params.getAll(FilterKey.MINIMUM_DAYS_IDLE).map(Number)[0]) - 1,
    ],
    dependants: params.getAll(FilterKey.DEPENDANTS),
    tagsMatch: params.getAll(MATCH_FILTER_NAMES.TAGS_MATCH),
    programmableTagsMatch: params.getAll(MATCH_FILTER_NAMES.PROGRAMMABLE_TAGS_MATCH),
  } as api.IGetAllParams;
};

export function* handleGetAllApplications(): any {
  const params = new URLSearchParams(window.location.search);
  let res: any;
  const auth: AuthState = yield select(getAuth);
  const config = yield select(getConfig);
  const intakeIdAuth: string = yield select(getIntakeId);
  const intakeId = params.get('intakeId') || intakeIdAuth;
  if (!intakeId && !isTesting) {
    return;
  }
  const { headerTab } = yield select(getUI);
  const intakeRange: IIntakeIdWithDates | undefined = yield select(getIntakeRange, intakeId!);
  const context = yield getContext('sagaContext');
  const idToken: any = yield call(context.getIdToken);

  try {
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleGetAllStudents');
      return;
    }

    const getAllApplicationsParams = createGetAllApplicationsParams({
      auth,
      config,
      intakeId,
      headerTab,
      intakeRange,
      idToken,
    });

    if (!getAllApplicationsParams) {
      return;
    }

    getAllApplicationsAbortController = new AbortController();
    // If user is an agent, use the agent leads endpoint instead

    res = yield call(api.getAllApplications, getAllApplicationsParams);

    yield put(setSelectedRows([]));
  } catch (err) {
    yield put(getStudentsFailure(err));
    return;
  } finally {
    if (yield cancelled()) {
      // the task was cancelled
      getAllApplicationsAbortController.abort();
      return;
    }
  }
  yield put(getStudentsSuccess(res.students, auth, config));

  yield put(getAllStudentsComplete());
}

function* watchGetApplications() {
  yield takeLatest(DashboardActionTypes.GET_APPLICATIONS, handleGetAllApplications);
}

function* GetApplicationsSaga() {
  yield all([fork(watchGetApplications)]);
}

export default GetApplicationsSaga;
